/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { css } from "@emotion/react";
import { useViewedScreen } from "@packages/analytics";
import {
  isRestrictedContentError,
  useRestrictedContentCheck,
} from "@packages/kids";
import type { Prayer, TrustedPrayer } from "@packages/sdk";
import { useRequestPrayer, useSession } from "@packages/sdk";
import type { CSSColorStrict, ThemeType } from "@packages/ui";
import {
  colors,
  constantSemanticColors,
  cssMediaQueryMinWidthTablet,
  Mask,
  numericPercentages,
  numericPixels,
  radius,
  spacing,
  Text,
  TextWithLinks,
  useIsDesktopViewport,
  zIndices,
} from "@packages/ui";
import { HallowDataTestId } from "@tools/playwright/global";
import Image from "next/image";
import { notFound } from "next/navigation";

import {
  LoadingPage,
  PrayerControls,
  PrayerQuickActions,
} from "../../../../components";

const containerStyles = (props: { color: CSSColorStrict }) =>
  css({
    backgroundColor: props.color,
    borderRadius: radius.none,
    width: numericPercentages[100],
    [cssMediaQueryMinWidthTablet]: {
      borderRadius: radius.xl,
      marginBottom: numericPixels[128],
      paddingBottom: spacing.xxxl,
    },
  });

const containerBottomStyles: Interpolation = {
  display: "flex",
  width: numericPercentages[100],
  flexDirection: "column",
  gap: spacing.l,
  [cssMediaQueryMinWidthTablet]: {
    flexDirection: "row",
    gap: spacing.xxl,
  },
};

const containerBottomLeftStyles: Interpolation = {
  display: "flex",
  flex: 1,
  flexDirection: "column",
  gap: spacing.m,
  width: numericPercentages[100],
  zIndex: zIndices.maskOver,
  paddingLeft: spacing.l,
  paddingRight: spacing.l,
  [cssMediaQueryMinWidthTablet]: {
    paddingLeft: spacing.xxl,
    paddingRight: spacing.none,
  },
};

const containerBottomRightStyles: Interpolation<ThemeType> = (theme) => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  gap: spacing.m,
  background: theme.semanticColors.background,
  paddingBottom: 128,
  paddingLeft: spacing.l,
  paddingRight: spacing.l,
  paddingTop: spacing.m,
  [cssMediaQueryMinWidthTablet]: {
    background: colors.transparent,
    paddingBottom: spacing.none,
    paddingLeft: spacing.none,
    paddingRight: spacing.xxl,
    paddingTop: spacing.none,
  },
});

const containerTopStyles: Interpolation = {
  position: "relative",
  width: spacing.full,
  paddingBottom: spacing.full,
  [cssMediaQueryMinWidthTablet]: {
    paddingBottom: "50%",
  },
};

const headerTextContainerStyles: Interpolation = {
  bottom: 0,
  display: "flex",
  flexDirection: "column",
  padding: `${spacing.xl} ${spacing.xxl}`,
  position: "absolute",
  zIndex: zIndices.maskOver,
};

const subtitleStyles: Interpolation = {
  color: constantSemanticColors.onColorVeryHigh,
};

const titleStyles: Interpolation = {
  color: constantSemanticColors.onColorHighest,
};

const descriptionStyles: Interpolation<ThemeType> = (theme) => ({
  height: "fit-content",
  whiteSpace: "normal",
  color: theme.semanticColors.primary,
  [cssMediaQueryMinWidthTablet]: {
    color: constantSemanticColors.onColorHighest,
  },
});

const imageStyles: Interpolation = {
  objectFit: "cover",
  objectPosition: "center",
  objectRepeat: "no-repeat",
  borderTopLeftRadius: radius.none,
  borderTopRightRadius: radius.none,
  [cssMediaQueryMinWidthTablet]: {
    borderTopLeftRadius: radius.xl,
    borderTopRightRadius: radius.xl,
  },
};

const maskStyles: Interpolation = {
  bottom: -1,
  position: "absolute",
};

const prayerQuickActionsMobileStyles: Interpolation = {
  display: "flex",
  [cssMediaQueryMinWidthTablet]: {
    display: "none",
  },
};

const prayerQuickActionsTabletStyles: Interpolation = {
  display: "none",
  [cssMediaQueryMinWidthTablet]: {
    display: "flex",
  },
};

const textMobileStyles: Interpolation = {
  flexDirection: "column",
  gap: spacing.xxs,
  justifyContent: "center",
  paddingBottom: spacing.s,
  display: "flex",
  [cssMediaQueryMinWidthTablet]: {
    display: "none",
  },
};

const textMobileSupertitleStyles: Interpolation = {
  color: constantSemanticColors.onColorVeryHigh,
  textAlign: "center",
};

const textMobileTitleStyles: Interpolation = {
  color: constantSemanticColors.onColorHighest,
  textAlign: "center",
};
type PrayerClientProps = {
  id: number;
  initialData?: Prayer | TrustedPrayer;
};

export const PrayerClient = ({ id, initialData }: PrayerClientProps) => {
  const isDesktop = useIsDesktopViewport();
  const { status } = useSession();

  const { query: queryPrayer } = useRequestPrayer({
    id,
    initialData,
    retryOnMount: false,
    retry: 0,
  });

  const isRestricted = useRestrictedContentCheck({ query: queryPrayer });

  useViewedScreen({
    screen: "session_detail",
    properties: {
      prayer: id,
      guide: queryPrayer.data?.selected_guide_id,
    },
    isReady:
      status === "unauthenticated" ||
      (status === "authenticated" && !!queryPrayer.data?.selected_guide_id),
  });

  if (
    (status === "authenticated" && queryPrayer.isLoading) ||
    (!queryPrayer.data && status === "loading") ||
    isRestrictedContentError(queryPrayer.error) ||
    isRestricted
  )
    return <LoadingPage />;

  /**
   * The SSR trusted endpoint is cached and may miss a prayer that exists but
   * isn't public yet. Once the client's authenticated fetch (real session,
   * uncached) has settled, trust it: no data means it really doesn't exist.
   * Restricted-content errors are excluded above so they resolve via the
   * dialog in useRestrictedContentCheck instead of 404ing.
   */
  if (!queryPrayer.data) notFound();

  return (
    <div
      css={containerStyles({
        color:
          (queryPrayer.data?.images?.color_hex as CSSColorStrict) ?? "#000000",
      })}
    >
      <div css={containerTopStyles}>
        <Image
          alt={queryPrayer.data?.title}
          fill={true}
          src={queryPrayer.data?.images?.large}
          css={imageStyles}
        />
        {isDesktop && (
          <div css={headerTextContainerStyles}>
            <Text
              as="p"
              data-testid={HallowDataTestId.CollectionTitle}
              type="detail"
              size="xl"
              css={subtitleStyles}
            >
              {queryPrayer.data?.collection?.title}
            </Text>
            <Text as="h1" type="headline" size="m" css={titleStyles}>
              {queryPrayer.data.title}
            </Text>
          </div>
        )}
        <Mask
          color={
            (queryPrayer.data?.images?.color_hex as CSSColorStrict) ?? "#000000"
          }
          direction="to top"
          size="xl"
          css={maskStyles}
        />
      </div>
      <div css={containerBottomStyles}>
        <div css={containerBottomLeftStyles}>
          <div css={textMobileStyles}>
            <Text as="h1" size="s" css={textMobileTitleStyles} type="headline">
              {queryPrayer.data?.title}
            </Text>
            <Text as="p" size="m" css={textMobileSupertitleStyles} type="title">
              {queryPrayer.data?.collection?.title}
            </Text>
          </div>
          <PrayerControls prayerId={id} />
          <PrayerQuickActions
            prayerId={id}
            variant="transparentWhite"
            css={prayerQuickActionsTabletStyles}
          />
        </div>
        <div css={containerBottomRightStyles}>
          {isDesktop ? (
            <TextWithLinks css={descriptionStyles}>
              {queryPrayer.data?.detailed_desc}
            </TextWithLinks>
          ) : (
            <TextWithLinks
              expandable={true}
              lineClamp={3}
              overflow={"ellipsis"}
              css={descriptionStyles}
            >
              {queryPrayer.data?.detailed_desc}
            </TextWithLinks>
          )}
          <PrayerQuickActions
            prayerId={id}
            css={prayerQuickActionsMobileStyles}
          />
        </div>
      </div>
    </div>
  );
};
